import classNames from "classnames";
import {
  ChangeEvent,
  DragEvent,
  forwardRef,
  HTMLAttributes,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from "react";

import BottomSheet from "src/components-v2/BottomSheet";
import Button from "src/components-v2/Button";
import Loader from "src/components-v2/Loader";
import Modal from "src/components-v2/Modal";
import {
  FileInputBox,
  FileInputBoxDragging,
  FileInputComponent,
  FileInputContent,
  FileInputField,
  FileInputPlaceholder,
  FileInputSeparatorText,
  FileInputSizeText,
  InvalidText,
  LoadingText,
  LoadingWrapper
} from "./FileInput.styled";
import ProgressLoading from "./components/ProgressLoading";

import { getTranslationManually } from "src/helpers/locale";
import { bytesToSize } from "src/helpers/number";
import { useIsMobileWidth } from "src/hooks/window";
import { PropsWithDataQaid } from "src/types/componentProps";
import IconUpload from "./icon-upload.png";
import { formatExtensions } from "src/helpers/string";

export type FileInputProps = Omit<HTMLAttributes<HTMLDivElement>, "onChange"> &
  PropsWithDataQaid<{
    /**
     * Field placeholder
     */
    placeholder?: string;

    /**
     * Set accepted file types (file extension separated by coma)
     * e.g: ".png, .jpg"
     */
    fileTypes?: string;

    /**
     * Set max file size (in bytes)
     */
    maxFileSize?: number;

    /**
     * Set field to be uploading state
     */
    isUploading?: boolean;

    /**
     * Set field to be disabled
     */
    disabled?: boolean;

    /**
     * Callback after file accepted
     */
    onChange?: (file: File | File[]) => void;

    /**
     * Callback when click
     */
    onClickButton?: () => void;

    /**
     * if true, reset file to null
     */
    resetFile?: boolean;

    /**
     * if true, user can choose multiple files
     */
    multiple?: boolean;

    /**
     * If file input supports multiple file, this prop will be used to validate the amount of files allowed to be uploaded
     */
    maxItems?: number;

    /**
     * Set button variant
     *
     * @type {"primary" | "mini"}
     */
    variant?: "primary" | "mini";

    /**
     * set loading duration (especially for variant mini)
     */
    loadingDuration?: number;

    /**
     * additional info before upload (if any)
     */
    additionalInfo?: Map<string, any> | null;

    /**
     * Set additional button variant
     *
     * @type {"single" | "multi"}
     */
    additionalInfoButton?: "single" | "multi";

    /**
     * if true, will show format info
     */
    withFormatInfo?: boolean;
  }>;

const defaultProps: Partial<FileInputProps> = {
  placeholder: getTranslationManually("Taruh di sini", "Drop here"),
  fileTypes: "",
  maxFileSize: 2097152,
  isUploading: false,
  disabled: false,
  resetFile: false,
  onChange: () => {},
  onClickButton: () => {},
  variant: "primary",
  loadingDuration: 10,
  additionalInfo: null,
  additionalInfoButton: "single",
  withFormatInfo: false
};

// function FileInput(props: FileInputProps) {
const FileInput = forwardRef<{ click?: () => void }, FileInputProps>((props, ref) => {
  const {
    className,
    placeholder,
    fileTypes,
    maxFileSize,
    isUploading,
    resetFile,
    disabled,
    onChange,
    onClickButton,
    "data-qaid": dataQaid,
    variant,
    loadingDuration,
    multiple,
    maxItems,
    additionalInfo,
    additionalInfoButton,
    withFormatInfo,
    ...restProps
  } = props;

  const [fileName, setFileName] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [dragCounter, setDragCounter] = useState(0);
  const [invalidText, setInvalidText] = useState("");
  const [showAdditionalInfo, setShowAdditionalInfo] = useState(false);

  const fileInputClass = classNames(
    "c-file-input",
    disabled && "is-disabled",
    variant == "mini" && "is-mini",
    !!invalidText?.length && "is-invalid",
    className
  );
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobileWidth = useIsMobileWidth();
  const texts = {
    uploading: getTranslationManually("Sedang mengunggah file", "Uploading file"),
    or: getTranslationManually("atau", "or"),
    button: getTranslationManually("Pilih File", "Select File"),
    maxSize: getTranslationManually("Ukuran maksimal file yang boleh diunggah", "Maximum allowed file size"),
    drop: getTranslationManually("Taruh di sini", "Drop here"),
    orDrop: getTranslationManually(" atau tarik file kesini", " or drop file here"),
    here: getTranslationManually(" berkas di sini", " file here"),
    isUploading: getTranslationManually("Mengunggah", "Uploading"),
    buttonUpload: getTranslationManually("Unggah", "Upload"),
    format: getTranslationManually(
      ` (${formatExtensions(fileTypes)}, maks. ${bytesToSize(maxFileSize, 0)})`,
      ` (${formatExtensions(fileTypes)}, max. ${bytesToSize(maxFileSize, 0)})`
    )
  };

  // Expose the fileInputRef to parent component
  useImperativeHandle(ref, () => ({
    click: () => {
      if (fileInputRef.current) {
        fileInputRef.current.click();
      }
    }
  }));

  function getInvalidText(file: File) {
    const fileExt = `.${file.name.split(".").pop().toLowerCase()}`;

    if (file.size > maxFileSize) {
      const oversizeText = getTranslationManually(
        `Ukuran file melebihi ${bytesToSize(maxFileSize)}. Mohon unggah ulang`,
        `File size exceed ${bytesToSize(maxFileSize)}. Please re-upload`
      );
      return oversizeText;
    }
    if (fileTypes !== "" && !fileTypes.includes(fileExt)) {
      const notAllowedFileType = getTranslationManually(
        `Format file tidak sesuai. Mohon unggah file dengan format ${formatExtensions(fileTypes)}`,
        `Format file not supported. Please upload file with format ${formatExtensions(fileTypes)}`
      );
      return notAllowedFileType;
    }

    return null;
  }

  function processFile(file: File) {
    const invalidText = getInvalidText(file);
    if (invalidText) {
      setInvalidText(invalidText);
      return;
    }

    setInvalidText("");
    setFileName(file.name);
    onChange(file);
  }

  function processFiles(files: File[] | FileList) {
    const names: string[] = [];
    if (maxItems && files.length > maxItems) {
      setInvalidText(
        getTranslationManually(
          `Jumlah file terlalu banyak, maksimal ${maxItems} file`,
          `File is too many, max ${maxItems} files`
        )
      );
      return;
    }
    for (const file of files as File[]) {
      const invalidText = getInvalidText(file);
      if (invalidText) {
        setInvalidText(invalidText);
        return;
      }
      names.push(file.name);
    }
    setInvalidText("");
    setFileName(names.join(", "));
    onChange(files as File[]);
  }

  function handleChange(event: ChangeEvent<HTMLInputElement>) {
    const files = event.target.files;
    setShowAdditionalInfo(false);
    if (multiple) {
      processFiles(files!);
      return;
    }
    const file = files![0];
    if (file) {
      processFile(file);
    }
  }

  function onDragEnter(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();

    if (!disabled) {
      setDragCounter(dragCounter + 1);
      if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
        setIsDragging(true);
      }
    }
  }

  function onDragLeave(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();

    if (!disabled) {
      const drc = dragCounter - 1;
      setDragCounter(drc);
      if (drc === 0) {
        setIsDragging(false);
      }
    }
  }

  function onDragOver(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();
  }

  function onDrop(event: DragEvent<HTMLDivElement>) {
    event.preventDefault();
    event.stopPropagation();

    if (!disabled) {
      setIsDragging(false);
      if (additionalInfo) {
        return setShowAdditionalInfo(true);
      }

      if (event.dataTransfer.files && event.dataTransfer.files.length > 0) {
        const files = event.dataTransfer.files;
        if (multiple) {
          processFiles(files);
        } else {
          const file = files[0];
          if (file) {
            processFile(file);
          }
        }
        setDragCounter(0);
      }
    }
  }

  useEffect(() => {
    if (resetFile) {
      fileInputRef.current.value = null;
      setInvalidText("");
    }
  }, [resetFile]);

  function handleClickInput() {
    if (additionalInfo) {
      setShowAdditionalInfo(true);
    } else {
      onClickButton();
      fileInputRef.current!.click();
    }
  }

  function contentAdditionalInfo() {
    if (additionalInfo) {
      return (
        <>
          {additionalInfo}
          {additionalInfoButton === "single" ? (
            <Button
              data-testid="qa-file-input-additional-info-button"
              block
              className="u-my-2"
              onClick={() => {
                onClickButton();
                fileInputRef.current.click();
              }}
              variant="secondary"
            >
              {getTranslationManually("Pilih File", "Choose File")}
            </Button>
          ) : (
            <div className="u-flex u-gap-2">
              <Button
                data-testid="qa-file-input-additional-info-back-button"
                block
                outline
                className="u-my-2"
                onClick={() => setShowAdditionalInfo(false)}
                variant="secondary"
              >
                {getTranslationManually("Kembali", "Back")}
              </Button>
              <Button
                block
                data-testid="qa-file-input-additional-info-button"
                className="u-my-2"
                onClick={() => {
                  onClickButton();
                  fileInputRef.current.click();
                }}
                variant="secondary"
              >
                {getTranslationManually("Unggah", "Upload")}
              </Button>
            </div>
          )}
        </>
      );
    }
  }

  // render additional info after click file input (not directly open file directory)
  function renderAdditionalInfo() {
    if (isMobileWidth) {
      return (
        <BottomSheet
          data-testid="qa-file-input-additional-info"
          show={showAdditionalInfo}
          close={() => setShowAdditionalInfo(false)}
          closeOnOverlay
          withoutHeader
        >
          {contentAdditionalInfo()}
        </BottomSheet>
      );
    }

    return (
      <Modal
        data-qaid="qa-file-input-additional-info"
        data-testid="qa-file-input-additional-info"
        onClose={() => setShowAdditionalInfo(false)}
        show={showAdditionalInfo}
        withoutHeader
        withoutCloseButton
      >
        {contentAdditionalInfo()}
      </Modal>
    );
  }

  useEffect(() => {
    if (isUploading) {
      setShowAdditionalInfo(false);
    }
  }, [isUploading]);

  if (variant === "mini") {
    return (
      <>
        <FileInputComponent className={fileInputClass} data-testid="qa-file-input" {...restProps}>
          {isUploading ? (
            <div className="u-italic u-p-2 u-flex u-justify-center u-items-center">
              <ProgressLoading duration={loadingDuration} />
              {texts.isUploading}...
            </div>
          ) : (
            <>
              <FileInputField
                data-testid="qa-file-input-field"
                data-qaid={dataQaid + "-field"}
                ref={fileInputRef}
                type="file"
                accept={fileTypes}
                onChange={handleChange}
                disabled={disabled}
              />
              <FileInputBox
                data-testid="qa-file-input-box"
                data-qaid={dataQaid + "-box"}
                className={classNames(disabled && "is-disabled", variant == "mini" && "is-mini")}
                onDragEnter={onDragEnter}
                onDragLeave={onDragLeave}
                onDragOver={onDragOver}
                onDrop={onDrop}
              >
                <FileInputContent className={classNames(isDragging && "is-hidden")}>
                  <div className="u-py-2 u-cursor-pointer u-truncate" onClick={handleClickInput}>
                    <img src={IconUpload} alt="icon-upload" className="u-mr-1" />
                    <span className="u-text-small u-text-neutral_secondary_text">
                      <span className="u-text-flip-orange">{texts.buttonUpload}</span>
                      {texts.here}
                      {withFormatInfo && texts.format}
                    </span>
                  </div>
                </FileInputContent>
                {isDragging && (
                  <FileInputBoxDragging
                    className={classNames(variant == "mini" && "is-mini")}
                    data-testid="qa-file-input-dragging-box"
                  >
                    {texts.drop}...
                  </FileInputBoxDragging>
                )}
              </FileInputBox>
            </>
          )}
          {invalidText !== "" && (
            <InvalidText data-testid="qa-file-input-invalid-text" className="u-mt-2 u--ml-3 u-text-xs u-text-left">
              {invalidText}
            </InvalidText>
          )}
        </FileInputComponent>
        {additionalInfo && renderAdditionalInfo()}
      </>
    );
  }

  return (
    <>
      <FileInputComponent
        className={`${fileInputClass} ${invalidText !== "" ? "u-mb-4" : "u-mb-0"}`}
        data-testid="qa-file-input"
        {...restProps}
      >
        {isUploading ? (
          <LoadingWrapper>
            <Loader />
            <LoadingText data-testid="qa-file-input-loading-text">
              {texts.uploading} <b>{fileName}...</b>
            </LoadingText>
          </LoadingWrapper>
        ) : (
          <>
            <FileInputField
              data-testid="qa-file-input-field"
              data-qaid={dataQaid + "-field"}
              ref={fileInputRef}
              type="file"
              accept={fileTypes}
              onChange={handleChange}
              multiple={multiple}
              disabled={disabled}
            />
            <FileInputBox
              data-testid="qa-file-input-box"
              data-qaid={dataQaid + "-box"}
              className={classNames(disabled && "is-disabled")}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
            >
              <FileInputContent className={classNames(isDragging && "is-hidden")}>
                <FileInputPlaceholder>{placeholder}</FileInputPlaceholder>
                <FileInputSeparatorText>{texts.or}</FileInputSeparatorText>
                <Button
                  data-testid="qa-file-input-button"
                  variant="secondary"
                  disabled={disabled}
                  onClick={handleClickInput}
                >
                  {texts.button}
                </Button>
                <FileInputSizeText>
                  {texts.maxSize}: <b>{bytesToSize(maxFileSize)}</b>
                </FileInputSizeText>
                {invalidText !== "" && (
                  <InvalidText data-testid="qa-file-input-invalid-text">{invalidText}</InvalidText>
                )}
                <Button
                  data-testid="qa-file-upload-button"
                  variant="tertiary"
                  className="u-p-2 u-text-neutral_secondary_text u-font-normal u-truncate"
                  onClick={() => {
                    onClickButton();
                    fileInputRef.current.click();
                  }}
                  disabled={disabled}
                >
                  <img src={IconUpload} alt="icon-upload" className="u-mr-2" />
                  <span className="u-text-flip-orange">{texts.buttonUpload}</span>
                  {isMobileWidth ? texts.here : texts.orDrop}
                  {withFormatInfo && texts.format}
                </Button>
              </FileInputContent>
              {isDragging && (
                <FileInputBoxDragging data-testid="qa-file-input-dragging-box">{texts.drop}...</FileInputBoxDragging>
              )}
            </FileInputBox>
          </>
        )}
      </FileInputComponent>
      {additionalInfo && renderAdditionalInfo()}
    </>
  );
});

FileInput.defaultProps = defaultProps;

export default FileInput;
